import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { FreshnessTag, IProfileAttributeSimilarMatch } from '@brushfire/types'
import { ConfirmationModalIcon } from '@/shared/components/confirmation/ConfirmationModalIcon'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'
import type { BuilderQuestion } from './types'
import { FRESHNESS_CHOICES } from './profile-attributes.api'

const fieldLabelSx = {
  display: 'block',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '20px',
  color: '#0f172a',
  mb: '8px',
} as const

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
    minHeight: 46,
  },
  '& .MuiOutlinedInput-input': {
    color: '#04183f',
    fontSize: '14px',
  },
} as const

type ConfirmSectionProps = Readonly<{
  name: string
  setName: (value: string) => void
  freshnessTag: FreshnessTag
  setFreshnessTag: (value: FreshnessTag) => void
  error: string | null
  busy: boolean
  onCancel: () => void
  onContinue: () => void
}>

export function ProfileUpdateConfirmSection({
  name,
  setName,
  freshnessTag,
  setFreshnessTag,
  error,
  busy,
  onCancel,
  onContinue,
}: ConfirmSectionProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: '32px', px: '24px', pb: '24px' }}>
      <Box
        aria-hidden
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: '#f1faff',
          color: '#008cd0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: '24px',
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography component="h2" sx={{ ...confirmationHeadlineSx, mb: '12px' }}>
        Are you sure you want to update this question as a Profile Update Question?
      </Typography>
      <Typography sx={{ ...confirmationBodySx, textAlign: 'center', maxWidth: 388, mx: 'auto', mb: '20px' }}>
        Responses to this question will be saved to the responder&apos;s profile and can be used for future audience targeting.
      </Typography>
      <Box
        sx={{
          bgcolor: '#f7fcff',
          border: '1px solid #d1f0ff',
          borderRadius: '8px',
          p: '19px 24px 24px',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '16px', lineHeight: '20px', color: '#0f172a', mb: '16px' }}>
          Profile Attribute
        </Typography>
        <Typography component="label" htmlFor="profile-update-attribute-name" sx={fieldLabelSx}>
          Profile Attribute Name
        </Typography>
        <TextField
          id="profile-update-attribute-name"
          data-testid="profile-update-attribute-name"
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
          sx={fieldSx}
        />
        <Typography sx={{ fontSize: '12px', lineHeight: '20px', color: '#9ca3af', mt: '8px', mb: '16px' }}>
          This will be the name of the profile attribute used for audience targeting
        </Typography>
        <Typography component="label" id="profile-update-freshness-label" sx={fieldLabelSx}>
          Freshness/ Refresh Frequency
        </Typography>
        <TextField
          select
          fullWidth
          data-testid="profile-update-freshness-tag"
          value={freshnessTag}
          onChange={(event) => setFreshnessTag(event.target.value as FreshnessTag)}
          SelectProps={{ labelId: 'profile-update-freshness-label' }}
          sx={fieldSx}
        >
          {FRESHNESS_CHOICES.map((choice) => (
            <MenuItem key={choice.tag} value={choice.tag}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            bgcolor: '#cfeeff',
            borderRadius: '8px',
            p: '10px',
            mt: '12px',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: '#008cd0', mt: '1px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#6b7a90' }}>
            Determines when this profile information becomes eligible to be asked again. Reaching the refresh date does not invalidate or delete the existing answer.
          </Typography>
        </Box>
        {error ? (
          <Typography role="alert" sx={{ color: 'error.main', mt: '12px', fontSize: '13px' }}>
            {error}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', mt: '22px' }}>
        <Button
          variant="outlined"
          data-testid="profile-update-confirm-cancel"
          onClick={onCancel}
          sx={confirmationSecondaryButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          data-testid="profile-update-confirm-continue"
          disabled={busy}
          onClick={onContinue}
          sx={{ ...confirmationPrimaryButtonSx, minWidth: 141 }}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
        </Button>
      </Box>
    </Box>
  )
}

type SimilarSectionProps = Readonly<{
  match: IProfileAttributeSimilarMatch | null
  error: string | null
  busy: boolean
  onCancel: () => void
  onUse: () => void
}>

export function ProfileUpdateSimilarSection({ match, error, busy, onCancel, onUse }: SimilarSectionProps) {
  return (
    <>
      <DialogTitle>Similar Profile Update Question Found</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{match?.attributeName}</Typography>
        <Typography sx={{ fontSize: '14px', mb: 1 }}>{match?.prompt}</Typography>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          Options: {match?.options.join(', ') || 'Free text'}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          Freshness: {match?.freshnessTag}
        </Typography>
        {error ? (
          <Typography sx={{ color: 'error.main', mt: 1, fontSize: '13px' }}>{error}</Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button data-testid="profile-update-similar-cancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button data-testid="profile-update-similar-use" variant="contained" disabled={busy} onClick={onUse}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Use this question'}
        </Button>
      </DialogActions>
    </>
  )
}

type UpdateSectionProps = Readonly<{
  question: BuilderQuestion
  name: string
  setName: (value: string) => void
  error: string | null
  busy: boolean
  onClose: () => void
  onSave: () => void
}>

export function ProfileUpdateEditSection({
  question,
  name,
  setName,
  error,
  busy,
  onClose,
  onSave,
}: UpdateSectionProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: '32px' }}>
      <ConfirmationModalIcon variant="edit" />
      <Box
        sx={{
          width: '100%',
          px: '24px',
          pt: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Typography component="h2" sx={confirmationHeadlineSx}>
          Update profile question
        </Typography>
        <Typography sx={{ ...confirmationBodySx, textAlign: 'center', maxWidth: 388 }}>
          Question text and options are saved. Freshness stays {question.freshnessTag ?? 'unchanged'}.
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 388 }}>
          <Typography component="label" htmlFor="profile-update-edit-name" sx={fieldLabelSx}>
            Profile Attribute Name
          </Typography>
          <TextField
            id="profile-update-edit-name"
            data-testid="profile-update-edit-name"
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={fieldSx}
          />
        </Box>
        {error ? (
          <Typography role="alert" sx={{ color: 'error.main', fontSize: '13px', textAlign: 'center' }}>
            {error}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          width: '100%',
          px: 3,
          py: 3,
        }}
      >
        <Button variant="outlined" onClick={onClose} sx={confirmationSecondaryButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          data-testid="profile-update-edit-save"
          disabled={busy}
          onClick={onSave}
          sx={confirmationPrimaryButtonSx}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Update'}
        </Button>
      </Box>
    </Box>
  )
}
